import Dexie, { Table } from 'dexie';

/**
 * Local geofence event for IndexedDB storage
 * Stored locally on device, synced to database on clock out
 */
export interface LocalGeofenceEvent {
  id?: number;                    // Auto-increment primary key
  entryId: number;                // TIME_ENTRIES.entry_id
  employeeId: number;             // EMPLOYEES.employee_id
  jobId: number;                  // JOBS.job_id
  jobName: string;                // Job name for display
  eventType: 'ENTER' | 'EXIT' | 'CLOCK_IN' | 'CLOCK_OUT' | 'AUTO_SWITCH';  // Event type
  eventTime: string;              // ISO timestamp (UTC)
  lat: number | null;
  lng: number | null;
  synced: boolean;                // Whether event has been synced to server
}

/**
 * Dexie database for storing geofence events locally
 */
export class GeofenceEventsDB extends Dexie {
  events!: Table<LocalGeofenceEvent, number>;

  constructor() {
    super('GeofenceEventsDB');

    this.version(1).stores({
      events: '++id, entryId, employeeId, jobId, eventType, eventTime, synced'
    });
  }

  /**
   * Add a new geofence event
   */
  async addEvent(event: Omit<LocalGeofenceEvent, 'id' | 'synced'>): Promise<number> {
    return this.events.add({
      ...event,
      synced: false
    });
  }

  /**
   * Get all unsynced events for a specific entry
   */
  async getUnsyncedEvents(entryId: number): Promise<LocalGeofenceEvent[]> {
    return this.events
      .where('entryId')
      .equals(entryId)
      .and(event => !event.synced)
      .toArray();
  }

  /**
   * Get all events for a specific entry
   */
  async getEventsByEntry(entryId: number): Promise<LocalGeofenceEvent[]> {
    return this.events
      .where('entryId')
      .equals(entryId)
      .sortBy('eventTime');
  }

  /**
   * Get the last EXIT event for a specific job and entry
   */
  async getLastExitEvent(entryId: number, jobId: number): Promise<LocalGeofenceEvent | undefined> {
    const events = await this.events
      .where('entryId')
      .equals(entryId)
      .and(event => event.jobId === jobId && event.eventType === 'EXIT')
      .toArray();

    if (events.length === 0) return undefined;

    // Sort by eventTime descending and return the most recent
    events.sort((a, b) => new Date(b.eventTime).getTime() - new Date(a.eventTime).getTime());
    return events[0];
  }

  /**
   * Get the last EXIT event from any job for an entry
   */
  async getLastExitEventForEntry(entryId: number): Promise<LocalGeofenceEvent | undefined> {
    const events = await this.events
      .where('entryId')
      .equals(entryId)
      .and(event => event.eventType === 'EXIT')
      .toArray();

    if (events.length === 0) return undefined;

    // Sort by eventTime descending and return the most recent
    events.sort((a, b) => new Date(b.eventTime).getTime() - new Date(a.eventTime).getTime());
    return events[0];
  }

  /**
   * Mark events as synced
   */
  async markEventsSynced(eventIds: number[]): Promise<void> {
    await this.events.where('id').anyOf(eventIds).modify({ synced: true });
  }

  /**
   * Clear all synced events for an entry (called after successful clock out)
   */
  async clearSyncedEvents(entryId: number): Promise<void> {
    await this.events
      .where('entryId')
      .equals(entryId)
      .and(event => event.synced)
      .delete();
  }

  /**
   * Clear all events for an entry (called on clock out success)
   */
  async clearEventsForEntry(entryId: number): Promise<void> {
    await this.events
      .where('entryId')
      .equals(entryId)
      .delete();
  }

  /**
   * Get all pending (unsynced) events for an employee
   */
  async getPendingEvents(employeeId: number): Promise<LocalGeofenceEvent[]> {
    return this.events
      .where('employeeId')
      .equals(employeeId)
      .and(event => !event.synced)
      .toArray();
  }

  /**
   * Clear all events (for debugging/reset)
   */
  async clearAll(): Promise<void> {
    await this.events.clear();
  }
}

// Export singleton instance
export const geofenceEventsDB = new GeofenceEventsDB();
